import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  Plus,
  Search,
  Users,
  Mail,
  Phone,
  MapPin,
  Heart,
  Award,
  Filter,
  MoreVertical,
  X,
  Star,
  Smile,
  Meh,
  Frown,
  Edit,
  Trash2,
} from "lucide-react";

const Talento = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("directorio");
  const [members, setMembers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [moments, setMoments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [showMomentModal, setShowMomentModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [editingMoment, setEditingMoment] = useState(null);
  const [newMember, setNewMember] = useState({
    name: "",
    email: "",
    role: "",
    direction: "",
    phone: "",
    bio: "",
    skills: [],
  });
  const [newMoment, setNewMoment] = useState({
    title: "",
    description: "",
    date: "",
    participants: [],
  });

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  const directions = [
    { id: "general", name: "Dirección General" },
    { id: "operaciones", name: "Dirección de Operaciones y Logística" },
    { id: "talento", name: "Dirección de Talento y Bienestar" },
    { id: "academia", name: "Dirección Académica y de Innovación Educativa" },
    { id: "comunicacion", name: "Dirección de Comunicación y Experiencia" },
    { id: "finanzas", name: "Dirección de Estrategia y Finanzas" },
    { id: "informacion", name: "Dirección de Investigación e Información" },
    { id: "tecnologia", name: "Dirección de Desarrollo Tecnológico e Innovación" },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [membersRes, surveysRes, momentsRes] = await Promise.all([
        axios.get(`${API}/members`, { withCredentials: true }),
        axios.get(`${API}/surveys`, { withCredentials: true }),
        axios.get(`${API}/human-moments`, { withCredentials: true }),
      ]);
      setMembers(membersRes.data || []);
      setSurveys(surveysRes.data || []);
      setMoments(momentsRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Initialize with empty arrays for production
      setMembers([]);
      setSurveys([]);
      setMoments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMember = async () => {
    if (!newMember.name.trim()) return;
    try {
      const response = await axios.post(`${API}/members`, newMember, { withCredentials: true });
      setMembers([...members, response.data]);
    } catch (error) {
      console.error("Error creating member:", error);
      const mockMember = { member_id: `m_${Date.now()}`, ...newMember, status: "active" };
      setMembers([...members, mockMember]);
    }
    setShowMemberModal(false);
    setEditingMember(null);
    setNewMember({ name: "", email: "", role: "", direction: "", phone: "", bio: "", skills: [] });
  };

  const handleEditMember = (member) => {
    setEditingMember(member);
    setNewMember({
      name: member.name,
      email: member.email,
      role: member.role || "",
      direction: member.direction || "",
      phone: member.phone || "",
      bio: member.bio || "",
      skills: member.skills || [],
    });
    setShowMemberModal(true);
  };

  const handleUpdateMember = async () => {
    if (!editingMember) return;
    try {
      await axios.put(`${API}/members/${editingMember.member_id}`, newMember, { withCredentials: true });
    } catch (error) {
      console.error("Error updating member:", error);
    }
    setMembers(members.map(m => m.member_id === editingMember.member_id ? { ...m, ...newMember } : m));
    setShowMemberModal(false);
    setEditingMember(null);
    setNewMember({ name: "", email: "", role: "", direction: "", phone: "", bio: "", skills: [] });
  };

  const handleDeleteMember = async (memberId) => {
    if (!window.confirm("¿Estás seguro de eliminar este miembro?")) return;
    try {
      await axios.delete(`${API}/members/${memberId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting member:", error);
    }
    setMembers(members.filter(m => m.member_id !== memberId));
  };

  const handleCreateMoment = async () => {
    if (!newMoment.title.trim()) return;
    try {
      const response = await axios.post(`${API}/human-moments`, newMoment, { withCredentials: true });
      setMoments([...moments, response.data]);
    } catch (error) {
      console.error("Error creating moment:", error);
      const mockMoment = { moment_id: `mo_${Date.now()}`, ...newMoment };
      setMoments([...moments, mockMoment]);
    }
    setShowMomentModal(false);
    setEditingMoment(null);
    setNewMoment({ title: "", description: "", date: "", participants: [] });
  };

  const handleEditMoment = (moment) => {
    setEditingMoment(moment);
    setNewMoment({
      title: moment.title,
      description: moment.description || "",
      date: moment.date || "",
      participants: moment.participants || [],
    });
    setShowMomentModal(true);
  };

  const handleUpdateMoment = async () => {
    if (!editingMoment) return;
    try {
      await axios.put(`${API}/human-moments/${editingMoment.moment_id}`, newMoment, { withCredentials: true });
    } catch (error) {
      console.error("Error updating moment:", error);
    }
    setMoments(moments.map(m => m.moment_id === editingMoment.moment_id ? { ...m, ...newMoment } : m));
    setShowMomentModal(false);
    setEditingMoment(null);
    setNewMoment({ title: "", description: "", date: "", participants: [] });
  };

  const handleDeleteMoment = async (momentId) => {
    if (!window.confirm("¿Estás seguro de eliminar este momento?")) return;
    try {
      await axios.delete(`${API}/human-moments/${momentId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting moment:", error);
    }
    setMoments(moments.filter(m => m.moment_id !== momentId));
  };

  const calculateENPS = () => {
    if (!surveys.length) return { score: 0, promoters: 0, passives: 0, detractors: 0 };
    
    const latestSurvey = surveys.find((s) => s.type === "enps");
    if (!latestSurvey || !latestSurvey.responses?.length) return { score: 0, promoters: 0, passives: 0, detractors: 0 };

    const responses = latestSurvey.responses;
    const promoters = responses.filter((r) => r.score >= 9).length;
    const passives = responses.filter((r) => r.score >= 7 && r.score <= 8).length;
    const detractors = responses.filter((r) => r.score <= 6).length;
    const total = responses.length;

    const score = Math.round(((promoters - detractors) / total) * 100);

    return { score, promoters, passives, detractors, total };
  };

  const enps = calculateENPS();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="talento">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Talento</h1>
          <p className="text-slate-500">Directorio de miembros y clima organizacional</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-panel p-1 inline-flex rounded-full">
        {[
          { id: "directorio", label: "Directorio", icon: Users },
          { id: "enps", label: "eNPS", icon: Heart },
          { id: "momentos", label: "Momentos Humanos", icon: Award },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-full transition-all ${
              activeTab === tab.id
                ? "bg-primary text-white"
                : "text-slate-600 hover:bg-slate-100"
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Directorio Tab */}
      {activeTab === "directorio" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar miembros..."
                className="w-full glass-input pl-10"
                data-testid="search-members-input"
              />
            </div>
            <button
              onClick={() => setShowMemberModal(true)}
              className="btn-primary flex items-center gap-2"
              data-testid="add-member-btn"
            >
              <Plus size={20} />
              Agregar Miembro
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {members.map((member) => (
              <div
                key={member.member_id}
                className="glass-panel p-6 card-hover"
                data-testid={`member-${member.member_id}`}
              >
                <div className="flex items-start gap-4">
                  {member.photo ? (
                    <img
                      src={member.photo}
                      alt={member.name}
                      className="w-16 h-16 rounded-2xl object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                      <span className="text-2xl font-bold text-white">
                        {member.name.charAt(0)}
                      </span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-slate-800">{member.name}</h3>
                    <p className="text-sm text-slate-500">{member.role}</p>
                    <span className="badge badge-primary mt-2 text-xs">
                      {directions.find((d) => d.id === member.direction)?.name}
                    </span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-slate-100 space-y-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Mail size={14} />
                    <span className="truncate">{member.email}</span>
                  </div>
                  {member.phone && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Phone size={14} />
                      <span>{member.phone}</span>
                    </div>
                  )}
                </div>

                {member.skills?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {member.skills.map((skill, i) => (
                      <span
                        key={i}
                        className="badge bg-slate-100 text-slate-600 text-xs"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                )}

                {isSuperAdmin && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                    <button
                      onClick={() => handleEditMember(member)}
                      className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2"
                    >
                      <Edit size={14} />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteMember(member.member_id)}
                      className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* eNPS Tab */}
      {activeTab === "enps" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="glass-panel p-6 text-center">
              <div className={`text-5xl font-bold mb-2 ${
                enps.score >= 50 ? "text-emerald-600" :
                enps.score >= 0 ? "text-amber-600" : "text-rose-600"
              }`}>
                {enps.score}
              </div>
              <p className="text-slate-500">Puntuación eNPS</p>
            </div>
            
            <div className="glass-panel p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Smile size={32} className="text-emerald-500" />
              </div>
              <div className="text-3xl font-bold text-slate-800">{enps.promoters}</div>
              <p className="text-slate-500">Promotores (9-10)</p>
            </div>
            
            <div className="glass-panel p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Meh size={32} className="text-amber-500" />
              </div>
              <div className="text-3xl font-bold text-slate-800">{enps.passives}</div>
              <p className="text-slate-500">Pasivos (7-8)</p>
            </div>
            
            <div className="glass-panel p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <Frown size={32} className="text-rose-500" />
              </div>
              <div className="text-3xl font-bold text-slate-800">{enps.detractors}</div>
              <p className="text-slate-500">Detractores (0-6)</p>
            </div>
          </div>

          <div className="glass-panel p-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Encuestas Activas</h3>
            {surveys.length > 0 ? (
              <div className="space-y-4">
                {surveys.map((survey) => (
                  <div
                    key={survey.survey_id}
                    className="flex items-center justify-between p-4 rounded-2xl bg-slate-50/50"
                  >
                    <div>
                      <h4 className="font-medium text-slate-800">{survey.title}</h4>
                      <p className="text-sm text-slate-500">
                        {survey.responses?.length || 0} respuestas
                      </p>
                    </div>
                    <span className={`badge ${
                      survey.status === "active" ? "badge-success" : "bg-slate-100 text-slate-600"
                    }`}>
                      {survey.status === "active" ? "Activa" : "Cerrada"}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-center py-8">No hay encuestas activas</p>
            )}
          </div>
        </div>
      )}

      {/* Momentos Humanos Tab */}
      {activeTab === "momentos" && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <button 
              onClick={() => {
                setEditingMoment(null);
                setNewMoment({ title: "", description: "", date: "", participants: [] });
                setShowMomentModal(true);
              }}
              className="btn-primary flex items-center gap-2" 
              data-testid="add-moment-btn"
            >
              <Plus size={20} />
              Registrar Momento
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {moments.map((moment) => (
              <div
                key={moment.moment_id}
                className="glass-panel p-6 card-hover"
                data-testid={`moment-${moment.moment_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Heart size={24} className="text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">{moment.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{moment.description}</p>
                    <div className="flex items-center gap-4 mt-4">
                      <span className="text-sm text-slate-400">
                        {moment.date ? new Date(moment.date).toLocaleDateString("es-ES", {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                        }) : "Sin fecha"}
                      </span>
                      <span className="text-sm text-slate-400">
                        {moment.participants?.length || 0} participantes
                      </span>
                    </div>
                  </div>
                </div>
                {isSuperAdmin && (
                  <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2">
                    <button
                      onClick={() => handleEditMoment(moment)}
                      className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2"
                    >
                      <Edit size={14} />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDeleteMoment(moment.moment_id)}
                      className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Add/Edit Member Modal */}
      {showMemberModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-lg animate-slide-up" data-testid="member-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                {editingMember ? "Editar Miembro" : "Agregar Miembro"}
              </h2>
              <button
                onClick={() => { setShowMemberModal(false); setEditingMember(null); }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Nombre completo
                  </label>
                  <input
                    type="text"
                    value={newMember.name}
                    onChange={(e) => setNewMember({ ...newMember, name: e.target.value })}
                    className="w-full glass-input"
                    placeholder="Juan Pérez"
                    data-testid="member-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newMember.email}
                    onChange={(e) => setNewMember({ ...newMember, email: e.target.value })}
                    className="w-full glass-input"
                    placeholder="juan@wca.org"
                    data-testid="member-email-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Rol
                  </label>
                  <input
                    type="text"
                    value={newMember.role}
                    onChange={(e) => setNewMember({ ...newMember, role: e.target.value })}
                    className="w-full glass-input"
                    placeholder="Coordinador"
                    data-testid="member-role-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Dirección
                  </label>
                  <select
                    value={newMember.direction}
                    onChange={(e) => setNewMember({ ...newMember, direction: e.target.value })}
                    className="w-full glass-input"
                    data-testid="member-direction-select"
                  >
                    <option value="">Seleccionar...</option>
                    {directions.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Teléfono
                </label>
                <input
                  type="tel"
                  value={newMember.phone}
                  onChange={(e) => setNewMember({ ...newMember, phone: e.target.value })}
                  className="w-full glass-input"
                  placeholder="+52 123 456 7890"
                  data-testid="member-phone-input"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowMemberModal(false); setEditingMember(null); }} className="btn-secondary">
                  Cancelar
                </button>
                <button 
                  onClick={editingMember ? handleUpdateMember : handleCreateMember} 
                  className="btn-primary" 
                  data-testid="save-member-btn"
                >
                  {editingMember ? "Guardar Cambios" : "Agregar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Moment Modal */}
      {showMomentModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-6 sm:p-8 w-full max-w-lg animate-slide-up" data-testid="moment-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">
                {editingMoment ? "Editar Momento" : "Registrar Momento Humano"}
              </h2>
              <button
                onClick={() => { setShowMomentModal(false); setEditingMoment(null); }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Título</label>
                <input
                  type="text"
                  value={newMoment.title}
                  onChange={(e) => setNewMoment({ ...newMoment, title: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Nombre del momento"
                  data-testid="moment-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Descripción</label>
                <textarea
                  value={newMoment.description}
                  onChange={(e) => setNewMoment({ ...newMoment, description: e.target.value })}
                  className="w-full glass-input min-h-[100px]"
                  placeholder="Describe el momento..."
                  data-testid="moment-description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Fecha</label>
                <input
                  type="date"
                  value={newMoment.date}
                  onChange={(e) => setNewMoment({ ...newMoment, date: e.target.value })}
                  className="w-full glass-input"
                  data-testid="moment-date-input"
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button onClick={() => { setShowMomentModal(false); setEditingMoment(null); }} className="btn-secondary">
                  Cancelar
                </button>
                <button 
                  onClick={editingMoment ? handleUpdateMoment : handleCreateMoment} 
                  disabled={!newMoment.title.trim()}
                  className="btn-primary" 
                  data-testid="save-moment-btn"
                >
                  {editingMoment ? "Guardar Cambios" : "Registrar"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Talento;
